import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
} from "@chakra-ui/react";
import React, { Children } from "react";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  showOkButton?: boolean;
  showYesButton?: boolean;
  onClick?: () => void;
  positiveButtonText?: string;
  negativeButtonText?: string;
  children?: React.ReactNode;
}

/**
 * A reusable dialog/modal component built with Chakra UI.
 *
 * @component
 * @param {Object} props - The component props
 * @param {boolean} props.open - Controls the visibility of the dialog
 * @param {() => void} props.onClose - Callback when the dialog is closed
 * @param {string} props.title - The title displayed in the dialog header
 * @param {string} props.description - The main content/message of the dialog
 * @param {boolean} [props.showOkButton=true] - Whether to show the OK button
 * @param {boolean} [props.showYesButton=true] - Whether to show the Yes button
 * @param {() => void} [props.onClick] - Callback for the primary action button
 * @param {string} [props.positiveButtonText] - Custom text for the positive action button
 * @param {string} [props.negativeButtonText] - Custom text for the negative action button
 * @param {React.ReactNode} [props.children] - Optional custom content to render in the dialog body
 *
 * @example
 * <Dialog
 *   open={isOpen}
 *   onClose={onClose}
 *   title="Confirm Action"
 *   description="Are you sure you want to proceed?"
 *   positiveButtonText="Confirm"
 *   negativeButtonText="Cancel"
 *   onClick={handleConfirm}
 * />
 */
const Dialog: React.FC<DialogProps> = ({
  open,
  onClose,
  title,
  description,
  onClick,
  showOkButton = true,
  showYesButton = true,
  positiveButtonText,
  negativeButtonText,
  children,
}) => {
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  if (!open) return null;

  return (
    <>
      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isOpen={open}
        isCentered
      >
        <AlertDialogOverlay />

        <AlertDialogContent>
          <AlertDialogHeader>{title}</AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            {description}
            {children && children}
          </AlertDialogBody>
          <AlertDialogFooter>
            {showOkButton && (
              <Button ref={cancelRef} onClick={onClose}>
                {negativeButtonText ? negativeButtonText : "Cancel"}
              </Button>
            )}
            {showYesButton && (
              <Button colorScheme="red" ml={3} onClick={onClick}>
                {positiveButtonText ? positiveButtonText : "Yes"}
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default Dialog;
